import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import CooperationPage from './pages/CooperationPage';
import FranchisePage from './pages/FranchisePage';
import HomePage from './pages/HomePage';
import NewsArticlePage from './pages/NewsArticlePage';
import NewsPage from './pages/NewsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import PocketKitPage from './pages/PocketKitPage';
import TwinkleColorPage from './pages/TwinkleColorPage';

const navItems = [
  { label: '首页', href: '/' },
  {
    label: '关于价探',
    href: '/about',
    children: [
      { label: '公司简介', href: '/about#company' },
      { label: '品牌理念', href: '/about#idea' },
      { label: '发展历程', href: '/about#history' },
      { label: '品牌实力', href: '/about#strength' },
      { label: '所获荣誉', href: '/about#honors' },
      { label: '合作伙伴', href: '/about#partners' },
      { label: '企业文化', href: '/about#culture' },
    ],
  },
  {
    label: '旗下品牌',
    href: '/brands',
    children: [
      { label: 'Twinkle Color', href: '/brands/twinkle-color' },
      { label: 'POCKET KIT 口袋妆备', href: '/brands/pocket-kit' },
    ],
  },
  { label: '招商加盟', href: '/franchise' },
  { label: '品牌合作', href: '/cooperation' },
  { label: '加入我们', href: '/careers' },
  { label: '新闻中心', href: '/news' },
];

const normalizePathname = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

const isFileProtocol = () => window.location.protocol === 'file:';

const getCurrentLocation = () => {
  if (isFileProtocol()) {
    const hash = window.location.hash.replace(/^#/, '');

    if (!hash) {
      return { path: '/', section: '' };
    }

    const [path, section = ''] = hash.split('#');

    return { path: normalizePathname(path || '/'), section };
  }

  return {
    path: normalizePathname(window.location.pathname),
    section: window.location.hash.replace(/^#/, ''),
  };
};

const NAV_TOP_VISIBILITY_OFFSET = 100;
const ABOUT_PARTNERS_CAROUSEL_LENGTH = 8;
const SCROLL_INTENT_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'PageDown',
  'PageUp',
  'Home',
  'End',
  ' ',
]);

function App() {
  const [currentLocation, setCurrentLocation] = useState(getCurrentLocation);
  const [aboutPartnersSlideIndex, setAboutPartnersSlideIndex] = useState(0);
  const [isNavVisible, setIsNavVisible] = useState(false);
  const [isAtTop, setIsAtTop] = useState(() => window.scrollY <= NAV_TOP_VISIBILITY_OFFSET);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const navShellRef = useRef<HTMLDivElement | null>(null);
  const userScrollIntentRef = useRef(false);
  const { path: currentPath, section: currentSection } = currentLocation;
  const usesFileRouting = isFileProtocol();

  const navigateTo = (href: string) => {
    if (usesFileRouting) {
      const nextHash = `#${href}`;

      if (nextHash === window.location.hash) {
        return;
      }

      window.location.hash = href;
      return;
    }

    const [pathname, hash = ''] = href.split('#');
    const nextPath = normalizePathname(pathname || '/');
    const nextHash = hash ? `#${hash}` : '';
    const nextUrl = `${nextPath}${nextHash}`;
    const currentUrl = `${currentPath}${currentSection ? `#${currentSection}` : ''}`;

    if (nextUrl === currentUrl) {
      return;
    }

    window.history.pushState({}, '', nextUrl);
    setCurrentLocation({
      path: nextPath,
      section: hash,
    });
  };

  const getInternalHref = (href: string) => (usesFileRouting ? `#${href}` : href);
  const isNavItemActive = (href: string) =>
    currentPath === href || (href !== '/' && currentPath.startsWith(`${href}/`));

  const hideNavIfUnfocused = () => {
    const activeElement = document.activeElement;

    if (
      navShellRef.current &&
      activeElement instanceof Node &&
      navShellRef.current.contains(activeElement)
    ) {
      return;
    }

    setIsNavVisible(false);
  };

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentLocation(getCurrentLocation());
    };

    const eventName = usesFileRouting ? 'hashchange' : 'popstate';

    window.addEventListener(eventName, handleLocationChange);

    return () => {
      window.removeEventListener(eventName, handleLocationChange);
    };
  }, [usesFileRouting]);

  useEffect(() => {
    userScrollIntentRef.current = false;
    setHasUserScrolled(false);
  }, [currentPath, currentSection]);

  useEffect(() => {
    const markScrollIntent = () => {
      userScrollIntentRef.current = true;
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (SCROLL_INTENT_KEYS.has(event.key)) {
        userScrollIntentRef.current = true;
      }
    };

    window.addEventListener('wheel', markScrollIntent, { passive: true });
    window.addEventListener('touchmove', markScrollIntent, { passive: true });
    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('wheel', markScrollIntent);
      window.removeEventListener('touchmove', markScrollIntent);
      window.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const nextIsAtTop = window.scrollY <= NAV_TOP_VISIBILITY_OFFSET;

      setIsAtTop(nextIsAtTop);

      if (!nextIsAtTop && userScrollIntentRef.current) {
        setHasUserScrolled(true);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (currentPath !== '/about') {
      setAboutPartnersSlideIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setAboutPartnersSlideIndex(
        (prevIndex) => (prevIndex + 1) % ABOUT_PARTNERS_CAROUSEL_LENGTH,
      );
    }, 3200);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentPath]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      if (currentPath === '/about' && currentSection) {
        const targetElement = document.querySelector<HTMLElement>(
          `[data-page-section="${currentSection}"]`,
        );

        if (targetElement) {
          targetElement.scrollIntoView({ block: 'start', behavior: 'auto' });
          return;
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [currentPath, currentSection]);

  useEffect(() => {
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>(
        '.home-section, .brand-section, .join-us-section, .global-bottom-section, .placeholder-card',
      ),
    );

    sections.forEach((section, index) => {
      section.style.setProperty('--reveal-delay', `${Math.min(index * 70, 280)}ms`);
      section.classList.remove('is-visible');
    });

    if (
      !sections.length ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof window.IntersectionObserver === 'undefined'
    ) {
      sections.forEach((section) => {
        section.classList.add('is-visible');
      });
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.16,
        rootMargin: '0px 0px -10% 0px',
      },
    );

    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, [currentPath, currentSection]);

  const renderCurrentPage = () => {
    if (currentPath === '/about') {
      return (
        <AboutPage
          aboutPartnersSlideIndex={aboutPartnersSlideIndex}
          setAboutPartnersSlideIndex={setAboutPartnersSlideIndex}
        />
      );
    }

    if (currentPath === '/careers') {
      return <CareersPage />;
    }

    if (currentPath === '/news/douyin-baike-6') {
      return <NewsArticlePage onNavigate={navigateTo} />;
    }

    if (currentPath === '/news') {
      return <NewsPage getInternalHref={getInternalHref} onNavigate={navigateTo} />;
    }

    if (currentPath === '/cooperation') {
      return <CooperationPage />;
    }

    if (currentPath === '/franchise') {
      return <FranchisePage />;
    }

    if (currentPath === '/brands/twinkle-color') {
      return <TwinkleColorPage />;
    }

    if (currentPath === '/brands/pocket-kit') {
      return <PocketKitPage />;
    }

    if (currentPath === '/') {
      return <HomePage />;
    }

    return <PlaceholderPage currentPath={currentPath} />;
  };

  return (
    <div className="App">
      <div
        className={`top-nav-shell${isAtTop || !hasUserScrolled || isNavVisible ? ' is-visible' : ''}`}
        ref={navShellRef}
        onMouseEnter={() => setIsNavVisible(true)}
        onMouseLeave={hideNavIfUnfocused}
        onFocusCapture={() => setIsNavVisible(true)}
        onBlurCapture={() => {
          window.requestAnimationFrame(hideNavIfUnfocused);
        }}
      >
        <div className="top-nav-hover-zone" aria-hidden="true" />

        <header className="top-nav-wrap">
          <nav className="top-nav">
            <a
              className="brand-mark"
              href={getInternalHref('/')}
              onClick={(event) => {
                event.preventDefault();
                navigateTo('/');
              }}
            >
              <span className="brand-mark-cn">价探</span>
              <span className="brand-mark-en">PriceTag</span>
            </a>

            <ul className="menu-list">
              {navItems.map((item) => (
                <li
                  className={`menu-item${isNavItemActive(item.href) ? ' is-active' : ''}`}
                  key={item.label}
                >
                  {item.children ? (
                    <button type="button">{item.label}</button>
                  ) : (
                    <a
                      href={getInternalHref(item.href)}
                      onClick={(event) => {
                        event.preventDefault();
                        navigateTo(item.href);
                      }}
                    >
                      {item.label}
                    </a>
                  )}
                  {item.children ? (
                    <div className="submenu">
                      {item.children.map((subItem) => (
                        <a
                          href={getInternalHref(subItem.href)}
                          key={subItem.label}
                          onClick={(event) => {
                            event.preventDefault();
                            navigateTo(subItem.href);
                          }}
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </nav>
        </header>
      </div>

      {renderCurrentPage()}
    </div>
  );
}

export default App;
